'use client';
import { useEffect, useId, useRef, useState } from 'react';
const digits = Array.from({ length: 10 }, (_, i) => String(i));
export const compact = (n: number) =>
  Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 })
    .format(n)
    .toLowerCase();
export const full = (n: number) => Intl.NumberFormat('en').format(n);
/**
 * Steps an integer toward `target` one unit at a time over roughly a second, so each
 * poll's increase rolls through the odometer instead of jumping. The first value and any
 * decrease (a new day, or a reload) snap immediately.
 */
export function useTween(target: number, duration = 900) {
  const [value, setValue] = useState(target);
  const shown = useRef(target);
  useEffect(() => {
    const from = shown.current;
    if (target <= from) {
      shown.current = target;
      setValue(target);
      return;
    }
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = Math.round(from + (target - from) * eased);
      if (next !== shown.current) {
        shown.current = next;
        setValue(next);
      }
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);
  return value;
}
/** Rolling digit display. Digits slide vertically; separators stay put. */
export function Odometer({ value, label }: { value: number; label?: string }) {
  const text = full(value);
  return (
    <span
      className="odometer"
      role="img"
      aria-label={`${text}${label ? ' ' + label : ''}`}
    >
      {text.split('').map((ch, i) =>
        /\d/.test(ch) ? (
          <span className="odometer-digit" key={text.length - i} aria-hidden>
            <span
              className="odometer-track"
              style={{ transform: `translateY(-${Number(ch) * 10}%)` }}
            >
              {digits.map((d) => (
                <span key={d}>{d}</span>
              ))}
            </span>
          </span>
        ) : (
          <span key={'s' + (text.length - i)} aria-hidden>
            {ch}
          </span>
        ),
      )}
    </span>
  );
}
/** Small area sparkline for hourly counts. */
export function Sparkline({
  points,
  width = 64,
  height = 30,
}: {
  points: number[];
  width?: number;
  height?: number;
}) {
  const gradient = useId();
  if (points.length < 2) return null;
  const max = Math.max(1, ...points);
  const pad = 4;
  const x = (i: number) => pad + (i / (points.length - 1)) * (width - pad * 2);
  const y = (v: number) => height - pad - (v / max) * (height - pad * 2);
  const line = points
    .map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
    .join(' ');
  const last = points[points.length - 1];
  return (
    <svg
      className="sparkline"
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      aria-hidden
    >
      <defs>
        <linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--accent)" stopOpacity="0.22" />
          <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        fill={`url(#${gradient})`}
        className="sparkline-area"
        d={`${line} L${x(points.length - 1).toFixed(1)},${height} L${x(0).toFixed(1)},${height} Z`}
      />
      <path className="sparkline-line" d={line} />
      <circle
        className="sparkline-dot"
        cx={x(points.length - 1)}
        cy={y(last)}
        r="3.5"
      />
    </svg>
  );
}
